import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { playTradeOpen } from "@/lib/sounds";
import {
  Minus, Plus, ArrowUp, ArrowDown, Clock, ChevronDown, Settings,
  TrendingUp, TrendingDown, Zap, BarChart2, Bell, ListOrdered, X
} from "lucide-react";

const BINARY_DURATIONS = [
  { label: "30 sn", sec: 30 }, { label: "1 dk", sec: 60 }, { label: "3 dk", sec: 180 },
  { label: "5 dk", sec: 300 }, { label: "15 dk", sec: 900 }, { label: "30 dk", sec: 1800 },
  { label: "1 sa", sec: 3600 },
];
const LEVERAGES = [1, 2, 5, 10, 20, 50, 100];
const QUICK_AMOUNTS = [10, 100, 500, 1000];

// Animated number that smoothly updates
function AnimatedNumber({ value, prefix = "", suffix = "", decimals = 2, className = "" }) {
  const [display, setDisplay] = useState(value);
  const [animating, setAnimating] = useState(false);
  const prev = useRef(value);

  useEffect(() => {
    if (prev.current !== value) {
      setAnimating(true);
      setDisplay(value);
      prev.current = value;
      const t = setTimeout(() => setAnimating(false), 300);
      return () => clearTimeout(t);
    }
  }, [value]);

  const formatted = typeof value === "number"
    ? value.toFixed(decimals)
    : value;

  return (
    <span className={`${className} ${animating ? "num-change" : ""}`} style={{ display: "inline-block" }}>
      {prefix}{formatted}{suffix}
    </span>
  );
}

// Circular progress ring for duration countdown
function DurationRing({ totalSec, elapsedSec, size = 32, strokeWidth = 3 }) {
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, (totalSec - elapsedSec) / totalSec));
  const dash = pct * circ;
  const isUrgent = pct < 0.2;

  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none"
        stroke={isUrgent ? "var(--iq-red)" : "var(--iq-orange)"}
        strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        style={{
          transition: "stroke-dasharray 0.5s linear, stroke 0.3s",
          filter: `drop-shadow(0 0 ${isUrgent ? 4 : 2}px ${isUrgent ? "var(--iq-red)" : "var(--iq-orange)"})`,
        }}
      />
    </svg>
  );
}

export default function TradePanel({ asset, price, onTradeOpened }) {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState("binary");
  const [amount, setAmount] = useState(1000);
  const [duration, setDuration] = useState(30);
  const [leverage, setLeverage] = useState(10);
  const [tp, setTp] = useState("");
  const [sl, setSl] = useState("");
  const [trailingStop, setTrailingStop] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDurations, setShowDurations] = useState(false);
  const [showLeverages, setShowLeverages] = useState(false);
  const [tradeAnim, setTradeAnim] = useState(null); // 'up' | 'down' | null
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [limitTargetPrice, setLimitTargetPrice] = useState("");
  const [alertPrice, setAlertPrice] = useState("");
  const [alertCondition, setAlertCondition] = useState("above");

  const maxLev = Math.min(...[asset?.max_leverage ?? 100, 500].filter(Boolean));
  const payout = asset?.payout ?? 87;
  const potentialProfit = (Number(amount) * payout / 100);
  const bal = user?.account_mode === "demo" ? user?.demo_balance : user?.real_balance;
  const currentDuration = BINARY_DURATIONS.find(d => d.sec === duration) || BINARY_DURATIONS[0];

  const handleTradeAnimation = (dir) => {
    setTradeAnim(dir);
    setTimeout(() => setTradeAnim(null), 700);
  };

  const submitBinary = async (direction) => {
    if (!asset || busy) return;
    handleTradeAnimation(direction === "call" ? "up" : "down");
    setBusy(true);
    try {
      const { data } = await api.post("/trades/binary/open", {
        symbol: asset.symbol, direction, amount: Number(amount), duration_sec: duration,
      });
      playTradeOpen();
      toast.success(`${asset.name} ${direction === "call" ? "YÜKSEK" : "AŞAĞI"} @ ${Number(data.entry_price).toFixed(asset.digits ?? 2)}`);
      await refreshUser(); onTradeOpened?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const submitCfd = async (direction) => {
    if (!asset || busy) return;
    handleTradeAnimation(direction === "buy" ? "up" : "down");
    setBusy(true);
    try {
      const body = { symbol: asset.symbol, direction, amount: Number(amount), leverage };
      if (tp) body.take_profit = Number(tp);
      if (sl) body.stop_loss = Number(sl);
      if (trailingStop) body.trailing_stop_pct = Number(trailingStop);
      const { data } = await api.post("/trades/cfd/open", body);
      playTradeOpen();
      toast.success(`${asset.name} ${direction === "buy" ? t("buy") : t("sell")} ${leverage}x @ ${Number(data.entry_price).toFixed(asset.digits ?? 2)}`);
      await refreshUser(); onTradeOpened?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const submitLimitOrder = async () => {
    if (!asset || !limitTargetPrice || busy) return;
    setBusy(true);
    try {
      const body = {
        symbol: asset.symbol,
        direction: mode === "binary" ? "call" : "buy",
        amount: Number(amount),
        target_price: Number(limitTargetPrice),
        trade_type: mode,
      };
      if (mode === "binary") body.duration_sec = duration;
      else body.leverage = leverage;
      await api.post("/trades/limit", body);
      toast.success("Limit emir oluşturuldu.");
      setShowLimitModal(false);
      setLimitTargetPrice("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const submitPriceAlert = async () => {
    if (!asset || !alertPrice || busy) return;
    setBusy(true);
    try {
      await api.post("/trades/alerts", {
        symbol: asset.symbol,
        condition: alertCondition,
        target_price: Number(alertPrice),
      });
      toast.success("Fiyat alarmı oluşturuldu.");
      setShowAlertModal(false);
      setAlertPrice("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="iq-right-panel" data-testid="iq-trade-panel" style={{ position: "relative" }}>
      {/* Trade direction flash */}
      {tradeAnim && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 100, pointerEvents: "none",
          background: tradeAnim === "up"
            ? "radial-gradient(ellipse at center top, rgba(0,230,118,0.18), transparent 70%)"
            : "radial-gradient(ellipse at center top, rgba(255,23,68,0.15), transparent 70%)",
          animation: "win-overlay 0.7s ease-out forwards",
          borderRadius: "inherit",
        }} />
      )}

      {/* ─── Header: asset name + live price ─── */}
      <div style={{
        padding: "10px 12px 8px",
        borderBottom: "1px solid var(--iq-border)",
        background: "linear-gradient(180deg, rgba(0,230,118,0.04), transparent)",
      }}>
        {asset ? (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: "linear-gradient(135deg, var(--iq-green-dim), rgba(0,230,118,0.05))",
                  border: "1px solid rgba(0,230,118,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700, color: "var(--iq-green)",
                }}>
                  {(asset.name || asset.symbol).slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{asset.name || asset.symbol}</div>
                  <div style={{ fontSize: 9, color: "var(--iq-text-dim)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    {asset.category === "crypto" ? "Dijital" : "Blitz"}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <div className="live-dot" />
                <span style={{ fontSize: 9, color: "var(--iq-green)", textTransform: "uppercase", letterSpacing: "0.06em" }}>CANLI</span>
              </div>
            </div>
            {price != null && (
              <div style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 22, fontWeight: 800, color: "#fff",
                letterSpacing: "0.02em", lineHeight: 1,
              }}>
                {formatPriceBig(price, asset.digits ?? 2)}
              </div>
            )}
          </>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div className="shimmer" style={{ height: 20, width: "60%" }} />
            <div className="shimmer" style={{ height: 26, width: "80%" }} />
          </div>
        )}
      </div>

      {/* ─── Mode Toggle ─── */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--iq-border)" }}>
        {[["binary", "Binary", <Zap key="b" style={{ width: 11, height: 11 }} />],
          ["cfd", "CFD", <BarChart2 key="c" style={{ width: 11, height: 11 }} />]].map(([m, label, icon]) => (
          <button
            key={m}
            data-testid={`mode-${m}`}
            onClick={() => setMode(m)}
            className={`iq-mode-tab ${mode === m ? "active" : ""}`}
          >
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
              {icon}{label}
            </span>
          </button>
        ))}
      </div>

      {/* ─── Investment Amount ─── */}
      <div style={{ padding: "10px 12px 6px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <span className="panel-label">Yatırım Tutarı</span>
          <Settings style={{ width: 12, height: 12, color: "var(--iq-text-dim)", cursor: "pointer", opacity: 0.6 }} />
        </div>
        <div className="iq-input-row">
          <button data-testid="amount-decrement"
            onClick={() => setAmount(Math.max(1, Number(amount) - (amount > 100 ? 100 : 10)))}>
            <Minus style={{ width: 14, height: 14 }} />
          </button>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
            <span style={{ color: "var(--iq-text-dim)", fontFamily: "'JetBrains Mono', monospace", fontSize: "16px", marginRight: "1px" }}>$</span>
            <input
              data-testid="amount-input"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{ width: "70px", textAlign: "center" }}
            />
          </div>
          <button data-testid="amount-increment"
            onClick={() => setAmount(Number(amount) + (amount >= 100 ? 100 : 10))}>
            <Plus style={{ width: 14, height: 14 }} />
          </button>
        </div>
        {/* Quick amounts */}
        <div style={{ display: "flex", gap: "4px", marginTop: "6px" }}>
          {QUICK_AMOUNTS.map((v) => (
            <button
              key={v}
              data-testid={`quick-${v}`}
              onClick={() => setAmount(v)}
              className={`iq-quick-btn ${Number(amount) === v ? "active" : ""}`}
            >${v}</button>
          ))}
        </div>
      </div>

      {mode === "binary" ? (
        <>
          {/* Duration Selector */}
          <div style={{ padding: "0 12px 6px", position: "relative" }}>
            <div className="panel-label" style={{ marginBottom: "6px", display: "flex", alignItems: "center", gap: 4 }}>
              <Clock style={{ width: 9, height: 9 }} /> Son Kullanma Tarihi
            </div>
            <button
              data-testid="duration-selector"
              onClick={() => setShowDurations(!showDurations)}
              className="iq-select-btn"
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <DurationRing totalSec={duration} elapsedSec={0} size={24} strokeWidth={2.5} />
                <span>≈ {currentDuration.label}</span>
              </div>
              <ChevronDown style={{
                width: 13, height: 13, color: "var(--iq-text-dim)",
                transform: showDurations ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }} />
            </button>
            {showDurations && (
              <div style={{
                position: "absolute", top: "100%", left: 12, right: 12,
                background: "var(--iq-panel)", border: "1px solid var(--iq-border-bright)",
                borderRadius: "6px", zIndex: 50, overflow: "hidden",
                boxShadow: "0 12px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,230,118,0.05)",
              }}>
                {BINARY_DURATIONS.map((d, i) => (
                  <button
                    key={d.sec}
                    data-testid={`duration-${d.sec}`}
                    onClick={() => { setDuration(d.sec); setShowDurations(false); }}
                    style={{
                      width: "100%", padding: "9px 12px", border: "none",
                      background: duration === d.sec ? "rgba(0, 230, 118, 0.1)" : "transparent",
                      color: duration === d.sec ? "var(--iq-green)" : "var(--iq-text)",
                      cursor: "pointer", textAlign: "left", fontSize: "12px",
                      fontFamily: "'JetBrains Mono', monospace", fontWeight: 500,
                      borderBottom: i < BINARY_DURATIONS.length - 1 ? "1px solid rgba(31,37,53,0.6)" : "none",
                      transition: "background 0.15s, color 0.15s",
                    }}
                    onMouseEnter={e => { if (duration !== d.sec) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                    onMouseLeave={e => { if (duration !== d.sec) e.currentTarget.style.background = "transparent"; }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <DurationRing totalSec={d.sec} elapsedSec={0} size={18} strokeWidth={2} />
                      {d.label}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Payout info */}
          <div style={{ padding: "0 12px 10px" }}>
            <div style={{
              background: "linear-gradient(135deg, rgba(0,230,118,0.06), rgba(0,230,118,0.02))",
              border: "1px solid rgba(0,230,118,0.12)",
              borderRadius: "8px",
              padding: "12px",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div className="panel-label" style={{ marginBottom: "4px" }}>Kâr Oranı</div>
                  <div style={{
                    fontSize: "32px", fontWeight: 900, color: "var(--iq-green)", lineHeight: 1,
                    fontFamily: "'JetBrains Mono', monospace",
                  }} className="payout-glow">
                    +{payout}%
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="panel-label" style={{ marginBottom: "4px" }}>{t("profit")}</div>
                  <div style={{
                    fontSize: "20px", fontWeight: 800, color: "var(--iq-green)",
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    +${potentialProfit.toFixed(0)}
                  </div>
                </div>
              </div>
              {/* Progress bar showing potential */}
              <div style={{ marginTop: "10px", height: "3px", background: "rgba(255,255,255,0.06)", borderRadius: "2px" }}>
                <div style={{
                  height: "100%", borderRadius: "2px",
                  width: `${Math.min(100, payout)}%`,
                  background: "linear-gradient(90deg, var(--iq-green), #00ff88)",
                  boxShadow: "0 0 8px var(--iq-green-glow)",
                  transition: "width 0.5s ease",
                }} />
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Leverage */}
          <div style={{ padding: "0 12px 10px", position: "relative" }}>
            <div className="panel-label" style={{ marginBottom: "6px" }}>
              Kaldıraç <span style={{ color: "var(--iq-orange)", fontSize: 9 }}>· max {maxLev}x</span>
            </div>
            <button
              onClick={() => setShowLeverages(!showLeverages)}
              className="iq-select-btn"
            >
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <TrendingUp style={{ width: 13, height: 13, color: "var(--iq-orange)" }} />
                {leverage}x
              </span>
              <ChevronDown style={{
                width: 13, height: 13, color: "var(--iq-text-dim)",
                transform: showLeverages ? "rotate(180deg)" : "none", transition: "transform 0.2s",
              }} />
            </button>
            {showLeverages && (
              <div style={{
                position: "absolute", top: "100%", left: 12, right: 12,
                background: "var(--iq-panel)", border: "1px solid var(--iq-border-bright)",
                borderRadius: "6px", zIndex: 50, overflow: "hidden",
                boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
              }}>
                {LEVERAGES.filter(l => l <= maxLev).map((l, i, arr) => (
                  <button
                    key={l}
                    data-testid={`leverage-${l}`}
                    onClick={() => { setLeverage(l); setShowLeverages(false); }}
                    style={{
                      width: "100%", padding: "9px 12px", border: "none",
                      background: leverage === l ? "rgba(255, 152, 0, 0.12)" : "transparent",
                      color: leverage === l ? "var(--iq-orange)" : "var(--iq-text)",
                      cursor: "pointer", textAlign: "left", fontSize: "12px",
                      fontFamily: "'JetBrains Mono', monospace", fontWeight: 500,
                      borderBottom: i < arr.length - 1 ? "1px solid rgba(31,37,53,0.6)" : "none",
                    }}
                  >{l}x</button>
                ))}
              </div>
            )}
          </div>

          {/* TP / SL / Trailing Stop */}
          <div style={{ padding: "0 12px 10px", display: "flex", gap: "8px" }}>
            {[["TP", tp, setTp, "tp-input", "var(--iq-green)"], ["SL", sl, setSl, "sl-input", "var(--iq-red)"], ["Trail%", trailingStop, setTrailingStop, "ts-input", "var(--iq-orange)"]].map(([label, val, setter, testid, color]) => (
              <div key={label} style={{ flex: 1 }}>
                <div style={{ fontSize: "9px", color, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "4px", fontWeight: 700 }}>{label}</div>
                <input
                  data-testid={testid} type="number" value={val}
                  onChange={(e) => setter(e.target.value)}
                  placeholder="—"
                  style={{
                    width: "100%", background: "var(--iq-bg)",
                    border: `1px solid ${val ? color + "66" : "var(--iq-border-bright)"}`,
                    borderRadius: "5px", color: val ? color : "#fff",
                    padding: "7px 8px", fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "11px", textAlign: "right", outline: "none",
                    transition: "border-color 0.2s",
                  }}
                />
              </div>
            ))}
          </div>

          {/* Notional */}
          <div style={{ padding: "0 12px 12px" }}>
            <div style={{
              background: "rgba(255,152,0,0.05)",
              border: "1px solid rgba(255,152,0,0.12)",
              borderRadius: "6px", padding: "10px",
            }}>
              {[
                ["Pozisyon Büyüklüğü", `$${(Number(amount) * leverage).toLocaleString()}`],
                ["Spread", `${((asset?.spread ?? 0) * 100).toFixed(3)}%`],
              ].map(([lbl, val]) => (
                <div key={lbl} style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "4px" }}>
                  <span style={{ color: "var(--iq-text-dim)" }}>{lbl}</span>
                  <span style={{ color: "#fff", fontFamily: "'JetBrains Mono', monospace" }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ─── Action Buttons ─── */}
      <div style={{ padding: "0 12px 10px", display: "flex", flexDirection: "column", gap: "6px", marginTop: "auto" }}>
        <button
          data-testid={mode === "binary" ? "call-button" : "buy-button"}
          disabled={busy || !asset}
          onClick={() => (mode === "binary" ? submitBinary("call") : submitCfd("buy"))}
          className="iq-btn-up"
          style={{ opacity: busy || !asset ? 0.5 : 1 }}
        >
          <ArrowUp style={{ width: 22, height: 22, filter: "drop-shadow(0 0 6px rgba(0,230,118,0.6))" }} />
          <span style={{ fontSize: 13, letterSpacing: "0.15em" }}>
            {mode === "binary" ? t("call").toUpperCase() : t("buy").toUpperCase()}
          </span>
          {mode === "binary" && price != null && (
            <span style={{ fontSize: 10, opacity: 0.75, fontFamily: "'JetBrains Mono', monospace" }}>
              @ {Number(price).toFixed(asset?.digits ?? 2)}
            </span>
          )}
        </button>

        <button
          data-testid={mode === "binary" ? "put-button" : "sell-button"}
          disabled={busy || !asset}
          onClick={() => (mode === "binary" ? submitBinary("put") : submitCfd("sell"))}
          className="iq-btn-down"
          style={{ opacity: busy || !asset ? 0.5 : 1 }}
        >
          <ArrowDown style={{ width: 22, height: 22, filter: "drop-shadow(0 0 6px rgba(255,23,68,0.6))" }} />
          <span style={{ fontSize: 13, letterSpacing: "0.15em" }}>
            {mode === "binary" ? t("put").toUpperCase() : t("sell").toUpperCase()}
          </span>
          {mode === "binary" && price != null && (
            <span style={{ fontSize: 10, opacity: 0.75, fontFamily: "'JetBrains Mono', monospace" }}>
              @ {Number(price).toFixed(asset?.digits ?? 2)}
            </span>
          )}
        </button>

        {/* Limit Order + Price Alert buttons */}
        <div style={{ display: "flex", gap: "6px", marginTop: "2px" }}>
          <button
            onClick={() => setShowLimitModal(true)}
            disabled={!asset}
            style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              padding: "7px 8px", fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.08em",
              fontWeight: 700, background: "rgba(255,152,0,0.1)", border: "1px solid rgba(255,152,0,0.3)",
              borderRadius: "4px", color: "var(--iq-orange)", cursor: asset ? "pointer" : "not-allowed",
              opacity: asset ? 1 : 0.5,
            }}
          >
            <ListOrdered style={{ width: 12, height: 12 }} /> Limit Emir
          </button>
          <button
            onClick={() => setShowAlertModal(true)}
            disabled={!asset}
            style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              padding: "7px 8px", fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.08em",
              fontWeight: 700, background: "rgba(100,181,246,0.1)", border: "1px solid rgba(100,181,246,0.3)",
              borderRadius: "4px", color: "#64b5f6", cursor: asset ? "pointer" : "not-allowed",
              opacity: asset ? 1 : 0.5,
            }}
          >
            <Bell style={{ width: 12, height: 12 }} /> Alarm
          </button>
        </div>
      </div>

      {/* ─── Limit Order Modal ─── */}
      {showLimitModal && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
        }} onClick={() => setShowLimitModal(false)}>
          <div style={{
            background: "var(--iq-panel)", border: "1px solid var(--iq-border-bright)",
            borderRadius: "8px", padding: "20px", width: "100%", maxWidth: "280px",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <span style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Limit Emir</span>
              <button onClick={() => setShowLimitModal(false)} style={{ background: "transparent", border: "none", color: "var(--iq-text-dim)", cursor: "pointer" }}><X style={{ width: 14, height: 14 }} /></button>
            </div>
            <div style={{ marginBottom: "10px" }}>
              <div style={{ fontSize: "9px", color: "var(--iq-text-dim)", textTransform: "uppercase", marginBottom: "4px" }}>Hedef Fiyat</div>
              <input type="number" value={limitTargetPrice} onChange={(e) => setLimitTargetPrice(e.target.value)}
                placeholder={price ? price.toFixed(asset?.digits ?? 2) : "0.00"}
                style={{ width: "100%", background: "var(--iq-bg)", border: "1px solid var(--iq-border-bright)", borderRadius: "5px", padding: "8px", fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", color: "#fff", outline: "none" }} />
            </div>
            <div style={{ fontSize: "10px", color: "var(--iq-text-dim)", marginBottom: "12px" }}>
              {asset?.name} • {mode === "binary" ? "Binary" : "CFD"} • ${amount}
            </div>
            <button onClick={submitLimitOrder} disabled={!limitTargetPrice || busy}
              style={{ width: "100%", padding: "10px", background: "var(--iq-orange)", color: "#000", border: "none", borderRadius: "5px", fontSize: "11px", fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Emir Oluştur
            </button>
          </div>
        </div>
      )}

      {/* ─── Price Alert Modal ─── */}
      {showAlertModal && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.85)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: "16px",
        }} onClick={() => setShowAlertModal(false)}>
          <div style={{
            background: "var(--iq-panel)", border: "1px solid var(--iq-border-bright)",
            borderRadius: "8px", padding: "20px", width: "100%", maxWidth: "280px",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <span style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Fiyat Alarmı</span>
              <button onClick={() => setShowAlertModal(false)} style={{ background: "transparent", border: "none", color: "var(--iq-text-dim)", cursor: "pointer" }}><X style={{ width: 14, height: 14 }} /></button>
            </div>
            <div style={{ display: "flex", gap: "6px", marginBottom: "10px" }}>
              {["above", "below"].map((c) => (
                <button key={c} onClick={() => setAlertCondition(c)}
                  style={{
                    flex: 1, padding: "8px", fontSize: "10px", textTransform: "uppercase", fontWeight: 700,
                    borderRadius: "4px", cursor: "pointer",
                    border: alertCondition === c ? "1px solid #64b5f6" : "1px solid var(--iq-border-bright)",
                    background: alertCondition === c ? "rgba(100,181,246,0.15)" : "transparent",
                    color: alertCondition === c ? "#64b5f6" : "var(--iq-text-dim)",
                  }}>
                  {c === "above" ? "Üstüne Çıkınca" : "Altına İnince"}
                </button>
              ))}
            </div>
            <div style={{ marginBottom: "12px" }}>
              <div style={{ fontSize: "9px", color: "var(--iq-text-dim)", textTransform: "uppercase", marginBottom: "4px" }}>Hedef Fiyat</div>
              <input type="number" value={alertPrice} onChange={(e) => setAlertPrice(e.target.value)}
                placeholder={price ? price.toFixed(asset?.digits ?? 2) : "0.00"}
                style={{ width: "100%", background: "var(--iq-bg)", border: "1px solid var(--iq-border-bright)", borderRadius: "5px", padding: "8px", fontFamily: "'JetBrains Mono', monospace", fontSize: "12px", color: "#fff", outline: "none" }} />
            </div>
            <button onClick={submitPriceAlert} disabled={!alertPrice || busy}
              style={{ width: "100%", padding: "10px", background: "#64b5f6", color: "#000", border: "none", borderRadius: "5px", fontSize: "11px", fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Alarm Oluştur
            </button>
          </div>
        </div>
      )}

      {/* ─── Balance ─── */}
      <div style={{
        padding: "10px 14px",
        borderTop: "1px solid var(--iq-border)",
        background: "rgba(0,0,0,0.15)",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div>
          <div className="panel-label">{t("account")}</div>
          <div style={{
            display: "flex", alignItems: "center", gap: 4, marginTop: 2,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: user?.account_mode === "demo" ? "var(--iq-orange)" : "var(--iq-green)",
            }} />
            <span style={{ fontSize: 10, color: user?.account_mode === "demo" ? "var(--iq-orange)" : "var(--iq-green)", fontWeight: 600 }}>
              {user?.account_mode === "demo" ? t("demo_account") : t("real_account")}
            </span>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="panel-label">{t("balance")}</div>
          <div style={{
            color: "#fff", fontFamily: "'JetBrains Mono', monospace",
            fontSize: 14, fontWeight: 700, marginTop: 2,
          }}>
            ${Number(bal ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper to format price with big last 2 digits
function formatPriceBig(price, digits) {
  if (price == null) return "—";
  const str = price.toFixed(digits);
  if (digits <= 2) return str;
  const bigPart = str.slice(0, -2);
  const smallPart = str.slice(-2);
  return (
    <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>
      {bigPart}<span style={{ fontSize: "1.5em", fontWeight: 900, color: "var(--iq-green)" }}>{smallPart}</span>
    </span>
  );
}
